import firebase from 'firebase/app';
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ResponseTokenInfo } from '../../services/api/models/response/responseTokenInfo';
import {
  signInWithSNS,
  signOut,
  SNSType,
} from '../../services/firebase/firebaseAuth';
import { useGetTokenInfoMutation } from '../../services/api/apiProvider';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth';
import { NoHashQueryStringUtils } from '../../common/utils/noHashQueryStringUtils';
import axios from 'axios';
import qs from 'qs';
import crypto from 'crypto';
import { showLoginModal } from '../login/login.slice';
import { reproio } from '../../common/utils/reproio';
import { AppState } from '../../store/store';
import {
  APP_BASE_URL,
  EVERY_AUTH_PROXY_SERVER_URL,
  EVERY_CLIENT_ID,
  EVERY_CLIENT_SECRET,
  EVERY_REDIRECT,
  IDP_ENDPOINT,
  LINE_AUTH_PROXY_SERVER_URL,
  LINE_CLIENT_ID,
  LINE_CLIENT_SECRET,
  LINE_REDIRECT,
  REPRO_SDK_TOKEN,
} from '../../loadEnv';

export const lineHashCode = 'q9TFYqB';
export const everyHashCode = 'BBsQhd3';

export interface AuthState {
  isAuthenticated: boolean;
  logInWithSNS: (type: SNSType) => Promise<void>;
  logInWithEveryId: () => Promise<void>;
  logInWithLINE: () => Promise<void>;
  loginFirebaseWithEveryIdCode: (code: string) => void;
  loginFirebaseWithLINECode: (code: string) => void;
  logOut: () => Promise<void>;
  registerComplete: () => void;
  isRegistering: boolean;
  userCoin: number;
  setUserCoin: React.Dispatch<React.SetStateAction<number>>;
}

const AuthContext = createContext<AuthState | null>(null);

export const useAuth = (): AuthState | null => {
  return useContext(AuthContext);
};

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [getTokenInfo] = useGetTokenInfoMutation();
  const [userCoin, setUserCoin] = useState<number>() as [
    number | undefined,
    React.Dispatch<React.SetStateAction<number>>,
  ];

  const currentUser = useSelector(
    (state: AppState) => state.profilePageState.currentUser,
  );

  // Set up repro
  // Must setUserId before call setup
  // If enable access app when no need login, need change this logic
  useMemo(() => {
    if (currentUser?.id) {
      reproio('setUserID', currentUser?.id);
      reproio('setup', process.env.REACT_APP_REPRO_SDK_TOKEN, {
        spa_mode: 'history',
      });
      const userGroup =
        parseInt(currentUser.id[currentUser.id.length - 1], 16) % 4;
      reproio('setIntUserProfile', 'UserGroup', userGroup);
    }
  }, [currentUser]);

  // this is TRUE if the user has logged in with SNS account, but not yet
  // registered with Every.Live
  const [isRegistering, setIsRegistering] = useState(false);

  /**
   * Check user has been registered?
   * @param user firebase user
   * @returns
   */
  const checkNewUser = async (user: firebase.User) => {
    // Get firebase user token
    const token = await user.getIdToken();

    // Check token to verify new user
    const result = (await getTokenInfo(token)) as { data: ResponseTokenInfo };
    const info = result.data;

    // User was registered
    if (info.every) {
      setIsAuthenticated(true);
    }
    // User is new
    else {
      setIsRegistering(true);
    }
  };

  useEffect(() => {
    if (userCoin !== undefined) {
      reproio('setIntUserProfile', 'NumberOfDiamondsHeld', userCoin);
    }
  }, [userCoin, currentUser?.id]);

  useEffect(() => {
    const unsubcribe = firebase.auth().onAuthStateChanged(async (user) => {
      unsubcribe();

      if (!user) {
        setIsAuthenticated(false);
      } else {
        await checkNewUser(user);
      }
      setLoading(false);
    });
  }, []);

  /**
   * Log in with SNS account.
   * @param type SNS type
   */
  const logInWithSNS = async (type: SNSType) => {
    const user = await signInWithSNS(type);
    if (user) {
      checkNewUser(user);
    }
  };

  const logInWithLINE = async (): Promise<void> => {
    const clientId = LINE_CLIENT_ID;
    const clientSecret = LINE_CLIENT_SECRET;
    const redirectUri = LINE_REDIRECT;

    if (!clientId || !redirectUri || !clientSecret) {
      return;
    }

    const queryParam = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      state: `${crypto.randomBytes(6).toString('hex')}${lineHashCode}`,
      scope: `profile openid`,
      nonce: `LINE`,
      prompt: 'consent',
      disable_auto_login: 'true',
      bot_prompt: 'normal',
      redirect_uri: redirectUri,
    });

    const lineAuthoriseURL =
      'https://access.line.me/oauth2/v2.1/authorize?' + queryParam.toString();

    window.location.href = lineAuthoriseURL;
  };

  /**
   * Login With LINE code
   * @param code code from LINE
   */
  const loginFirebaseWithLINECode = (code: string) => {
    const clientId = LINE_CLIENT_ID;
    const clientSecret = LINE_CLIENT_SECRET;
    const redirectUri = LINE_REDIRECT;
    const proxyAuthServer = LINE_AUTH_PROXY_SERVER_URL;

    if (!clientId || !redirectUri || !clientSecret || !proxyAuthServer) {
      return;
    }

    const reqBody = {
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: redirectUri,
      client_id: clientId,
      client_secret: clientSecret,
    };
    const requestConfig = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    axios
      .post(
        'https://api.line.me/oauth2/v2.1/token',
        qs.stringify(reqBody),
        requestConfig,
      )
      .then((res) => {
        axios
          .post(proxyAuthServer, {
            token: res.data.access_token,
          })
          .then((res) => {
            signInWithCustomToken(res.data.token);
          });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /**
   * Login With EveryID code
   * @param code code from EveryID
   */
  const loginFirebaseWithEveryIdCode = (code: string) => {
    const idpEndPoint = IDP_ENDPOINT;
    const clientId = EVERY_CLIENT_ID;
    const redirectUri = EVERY_REDIRECT;
    const clientSecret = EVERY_CLIENT_SECRET;
    const proxyAuthServer = EVERY_AUTH_PROXY_SERVER_URL;

    if (
      !idpEndPoint ||
      !clientId ||
      !redirectUri ||
      !clientSecret ||
      !proxyAuthServer
    ) {
      return;
    }

    if (code === null) {
      return;
    }

    const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    const authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    const notifier = new AuthorizationNotifier();
    authorizationHandler.setAuthorizationNotifier(notifier);
    notifier.setAuthorizationListener((request, response, error) => {
      console.log('Authorization request complete ', request, response, error);
      if (response) {
        console.log(`Authorization Code  ${response.code}`);

        const extras: any = {};
        if (request && request.internal) {
          extras.code_verifier = request.internal.code_verifier;
          extras.client_secret = clientSecret;
        }

        const tokenRequest = new TokenRequest({
          client_id: clientId,
          redirect_uri: redirectUri,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: response.code,
          refresh_token: undefined,
          extras,
        });

        AuthorizationServiceConfiguration.fetchFromIssuer(
          idpEndPoint,
          new FetchRequestor(),
        )
          .then((oResponse) => {
            const configuration = oResponse;
            return tokenHandler.performTokenRequest(
              configuration,
              tokenRequest,
            );
          })
          .then((oResponse) => {
            axios
              .post(proxyAuthServer, {
                token: oResponse.accessToken,
              })
              .then((res) => {
                signInWithCustomToken(res.data.token);
              });
          })
          .catch((oError) => {
            console.log(oError);
          });
      }
    });

    authorizationHandler.completeAuthorizationRequestIfPossible();
  };

  /**
   * Start login with Every ID
   */
  const logInWithEveryId = async (): Promise<void> => {
    const idpEndPoint = IDP_ENDPOINT;
    const clientId = EVERY_CLIENT_ID;
    const clientSecret = EVERY_CLIENT_SECRET;
    const redirectUri = EVERY_REDIRECT;

    if (!idpEndPoint || !clientId || !redirectUri || !clientSecret) {
      return;
    }

    const authServiceConfiguration =
      await AuthorizationServiceConfiguration.fetchFromIssuer(idpEndPoint);

    // create a request
    const request = new AuthorizationRequest({
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: 'openid',
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: `${crypto.randomBytes(6).toString('hex')}${everyHashCode}`,
      extras: {
        prompt: 'login',
        access_type: 'offline',
        client_secret: clientSecret,
      },
    });

    const authorizationHandler = new RedirectRequestHandler();

    // make authorization request
    authorizationHandler.performAuthorizationRequest(
      authServiceConfiguration,
      request,
    );
  };

  const signInWithCustomToken = (token: string): void => {
    firebase
      .auth()
      .signInWithCustomToken(token)
      .then((): void => {
        // go to top page after sign in with custom token
        window.location.href = `${window.location.origin}/${APP_BASE_URL}`;
      });
  };

  /**
   * Log out
   */
  const logOut = async () => {
    await signOut();
    setIsRegistering(false);
    history.push('/login');
    setIsAuthenticated(false);
    dispatch(showLoginModal(true));
  };

  /**
   * After register is complete, set authenticated.
   */
  const registerComplete = () => {
    setIsAuthenticated(true);
  };

  const value: AuthState = {
    isAuthenticated,
    logInWithSNS,
    logInWithEveryId,
    logInWithLINE,
    loginFirebaseWithLINECode,
    loginFirebaseWithEveryIdCode,
    logOut,
    registerComplete,
    isRegistering,
    userCoin: userCoin || 0,
    setUserCoin,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
